/**
 * AIO Content for Features Pillar Page (/guides/features)
 */

export const featuresPillarAIO = {
  quickAnswer: "XtreamCube IPTV premium features include 20,000+ live channels with 4K/HDR streaming, 7-day catch-up TV, cloud DVR recording, multi-device support (up to 5 simultaneous streams), electronic program guide (EPG), anti-freeze technology, VPN compatibility, and 24/7 priority customer support. All features included in premium subscriptions starting at $14.99/month.",
  
  keyFeatures: [
    {
      name: "20,000+ Live Channels",
      icon: "📺",
      description: "Massive content library spanning sports, movies, series, news, and entertainment from 150+ countries.",
      included: ["All channels", "HD quality", "4K channels", "VOD library"],
      premium: true
    },
    {
      name: "4K & HDR Streaming",
      icon: "🎬",
      description: "Ultra-high-definition 4K resolution at 60fps with HDR support for cinema-quality viewing experience.",
      included: ["4K channels", "HDR support", "60fps playback", "Adaptive bitrate"],
      premium: true
    },
    {
      name: "7-Day Catch-Up TV",
      icon: "⏰",
      description: "Watch any program aired within the past 7 days without pre-recording. Never miss your favorite shows.",
      included: ["7-day archive", "Instant playback", "All channels", "No storage needed"],
      premium: true
    },
    {
      name: "Cloud DVR Recording",
      icon: "☁️",
      description: "Record unlimited shows and movies to cloud storage. Watch anytime, anywhere, on any device.",
      included: ["Unlimited storage", "Multi-device access", "Auto-delete options", "Series recording"],
      premium: true
    },
    {
      name: "Multi-Device Support",
      icon: "📱",
      description: "Stream on up to 5 devices simultaneously. Works on 50+ device types including TV, phone, tablet, PC.",
      included: ["5 simultaneous streams", "50+ devices", "Unlimited installs", "Family sharing"],
      premium: true
    },
    {
      name: "Electronic Program Guide",
      icon: "📅",
      description: "Comprehensive EPG with 7-day schedules, program descriptions, cast info, and ratings updated daily.",
      included: ["7-day EPG", "Program details", "Cast/crew info", "Daily updates"],
      premium: false
    },
    {
      name: "Anti-Freeze Technology",
      icon: "⚡",
      description: "Intelligent buffering and server redundancy prevents interruptions and ensures smooth streaming.",
      included: ["Smart buffering", "Auto-failover", "Quality optimization", "99.9% uptime"],
      premium: true
    },
    {
      name: "VPN Compatible",
      icon: "🔒",
      description: "Works seamlessly with all major VPN services for enhanced privacy and access to geo-restricted content.",
      included: ["All VPNs supported", "No speed throttling", "Secure streaming", "Privacy protection"],
      premium: false
    },
    {
      name: "Parental Controls",
      icon: "👨‍👩‍👧‍👦",
      description: "Age-based content filtering, channel locking, and PIN protection for safe family viewing.",
      included: ["Content filtering", "Channel locking", "PIN protection", "View history"],
      premium: false
    },
    {
      name: "24/7 Priority Support",
      icon: "💬",
      description: "Premium subscribers get priority customer support with average 30-minute response time via WhatsApp/email.",
      included: ["Priority queue", "30-min response", "WhatsApp support", "Email support"],
      premium: true
    }
  ],
  
  comparisonTable: {
    headers: ["Feature", "Basic Plan", "Standard Plan", "Premium Plan"],
    rows: [
      ["Live Channels", "10,000+", "15,000+", "20,000+"],
      ["4K Streaming", "❌", "Limited", "✅ Full"],
      ["Catch-Up TV", "❌", "3 days", "7 days"],
      ["Cloud DVR", "❌", "❌", "✅ Unlimited"],
      ["Simultaneous Streams", "1 device", "2 devices", "5 devices"],
      ["EPG", "✅", "✅", "✅ Enhanced"],
      ["Anti-Freeze Tech", "❌", "✅", "✅"],
      ["Priority Support", "❌", "❌", "✅ 30-min"],
      ["Monthly Price", "$9.99", "$ 14.99", "$19.99"],
      ["Best For", "Single user", "Couples", "Families"]
    ]
  },
  
  faqItems: [
    {
      question: "What features are included in XtreamCube IPTV subscriptions?",
      answer: "All XtreamCube subscriptions include 10,000-20,000+ live channels, electronic program guide (EPG), multi-device support, VPN compatibility, and parental controls. Premium plans ($19.99/month) add 4K/HDR streaming, 7-day catch-up TV, unlimited cloud DVR, 5 simultaneous streams, anti-freeze technology, and priority 24/7 support."
    },
    {
      question: "What is catch-up TV and how does it work?",
      answer: "Catch-up TV lets you watch any program aired within the past 7 days without pre-recording. Browse the EPG, select any show from the past week, and play instantly. No storage space needed as all content is streamed from cloud servers. Available on 10,000+ channels in premium plans."
    },
    {
      question: "How does cloud DVR recording work?",
      answer: "Cloud DVR records shows/movies to XtreamCube's servers instead of your device. Record unlimited content with no storage limits. Schedule recordings from EPG, set series recordings for new episodes, and watch from any device. Recordings auto-delete after 30 days or manually managed. Premium feature only."
    },
    {
      question: "Can I stream on multiple devices at the same time?",
      answer: "Yes! Basic plans include 1 simultaneous stream, Standard plans include 2 streams, and Premium plans include 5 simultaneous streams. You can install the app on unlimited devices, but only the specified number can stream at once. Perfect for families or multi-room viewing."
    },
    {
      question: "What is anti-freeze technology?",
      answer: "Anti-freeze technology uses intelligent buffering, automatic server failover, and adaptive bitrate streaming to prevent buffering, freezing, and interruptions. If one server has issues, your stream automatically switches to a backup server in milliseconds. Ensures 99.9% uptime and smooth 4K playback."
    },
    {
      question: "Does 4K streaming require faster internet?",
      answer: "Yes, 4K streaming requires 25+ Mbps internet speed for smooth playback. HD channels need 10+ Mbps. XtreamCube's adaptive bitrate automatically adjusts quality based on your connection speed, preventing buffering. Speed test recommendations: 10 Mbps for HD, 25 Mbps for 4K, 50 Mbps for multiple 4K streams."
    },
    {
      question: "Is VPN required for IPTV streaming?",
      answer: "VPN is not required but recommended for privacy and accessing geo-restricted content. XtreamCube works with all major VPN services (ExpressVPN, NordVPN, Surfshark) without speed throttling. VPN adds encryption and allows access to regional channels from anywhere worldwide."
    },
    {
      question: "What's the difference between Standard and Premium plans?",
      answer: "Standard ($14.99) includes 15,000+ channels, limited 4K, 3-day catch-up, 2 devices. Premium ($19.99) includes 20,000+ channels, full 4K/HDR, 7-day catch-up, unlimited cloud DVR, 5 devices, anti-freeze tech, and priority support. Premium recommended for families and 4K TV owners."
    }
  ]
};
